/* eslint-disable */
import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Typography,
  Chip,
  Box,
  makeStyles,
} from '@material-ui/core';
import {
  Settings as SettingsIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@material-ui/icons';
import { Field } from 'formik';
import MultiSelectPopover from './MultiSelectPopover';

const useStyles = makeStyles((theme) => ({
  container: {
    border: '1px solid #e1e5e9',
    borderRadius: 8,
    padding: theme.spacing(2),
    backgroundColor: '#fafbfc',
    marginBottom: theme.spacing(2),
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#00A8FF',
      backgroundColor: '#f8fafc',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
    gap: theme.spacing(1.5),
  },
  titleSection: {
    flex: 1,
  },
  title: {
    fontWeight: 600,
    color: '#2d3748',
    fontSize: '0.95rem',
  },
  subtitle: {
    color: '#718096',
    fontSize: '0.8rem',
    marginTop: theme.spacing(0.5),
  },
  selectedItems: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
  chip: {
    backgroundColor: '#e6f7ff',
    color: '#1890ff',
    fontSize: '0.75rem',
    height: 26,
    '& .MuiChip-label': {
      paddingLeft: 10,
      paddingRight: 10,
    },
  },
  automationChip: {
    backgroundColor: '#f6ffed',
    color: '#52c41a',
  },
  configureButton: {
    textTransform: 'none',
    borderRadius: 6,
    padding: theme.spacing(0.75, 2),
    fontSize: '0.875rem',
    minWidth: 120,
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    color: '#374151',
    '&:hover': {
      backgroundColor: '#f3f4f6',
      borderColor: '#00A8FF',
      color: '#00A8FF',
    },
  },
  buttonEndIcon: {
    marginLeft: theme.spacing(0.5),
    transition: 'transform 0.2s ease',
  },
  buttonEndIconOpen: {
    transform: 'rotate(180deg)',
  },
  emptyState: {
    color: '#718096',
    fontStyle: 'italic',
    fontSize: '0.8rem',
  },
  errorState: {
    borderColor: '#f56565 !important',
    backgroundColor: '#fef5e7 !important',
  },
  errorText: {
    color: '#e53e3e',
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
  showMoreChip: {
    backgroundColor: '#f0f2ff',
    color: '#4a5568',
    fontSize: '0.75rem',
    height: 26,
    cursor: 'default',
    '&:hover': {
      backgroundColor: '#f0f2ff',
    },
    '&:focus': {
      backgroundColor: '#f0f2ff',
    },
  },
}));

const FormikMultiSelectPopover = ({
  name,
  label,
  subtitle,
  availableItems,
  showAutomation,
  searchPlaceholder,
  dialogTitle,
  clearBackendError,
  backendError,
  maxChipsDisplay = 5,
  ...props
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const buttonRef = useRef(null);

  const handleOpenPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleApply = (selectedItems, form) => {
    form.setFieldValue(name, selectedItems);
    if (clearBackendError) {
      clearBackendError(name);
    }
    setAnchorEl(null);
  };

  const getSelectedItemsWithData = (selectedItems) => {
    if (!Array.isArray(selectedItems)) return [];
    return selectedItems.map(selectedItem => {
      const fullItem = availableItems.find(item => item.key === selectedItem.key);
      return {
        ...selectedItem,
        ...fullItem,
      };
    }).filter(Boolean);
  };

  const normalizeSelectedItems = (selectedItems) => {
    if (!Array.isArray(selectedItems)) return [];

    const normalizedItems = selectedItems.filter(i => !!i ).map(item => {
      // If item is already in the correct format (has enableAutomation and key)
      if (typeof item === 'object' && item.hasOwnProperty('key')) {
        return {
          key: item.key,
          enableAutomation: item.enableAutomation || false,
        };
      }

      // If item is just a string key (resources format)
      if (typeof item === 'string') {
        return {
          key: item,
          enableAutomation: false,
        };
      }

      return null;
    }).filter(Boolean);

    // Remove duplicates based on key
    const uniqueItems = [];
    const seenKeys = new Set();

    normalizedItems.forEach(item => {
      if (!seenKeys.has(item.key)) {
        seenKeys.add(item.key);
        uniqueItems.push(item);
      }
    });

    return uniqueItems;
  };

  const hasError = !!backendError;
  const isOpen = Boolean(anchorEl);

  return (
    <Field name={name}>
      {({ field, form }) => {
        const selectedItems = normalizeSelectedItems(field.value || []);
        const selectedItemsWithData = getSelectedItemsWithData(selectedItems);
        const visibleChips = selectedItemsWithData.slice(0, maxChipsDisplay);
        const remainingCount = selectedItemsWithData.length - maxChipsDisplay;

        return (
          <>
            <div className={`${classes.container} ${hasError ? classes.errorState : ''}`}>
              <div className={classes.header}>
                <div className={classes.titleSection}>
                  <Typography className={classes.title}>
                    {label}
                  </Typography>
                  {subtitle && (
                    <Typography className={classes.subtitle}>
                      {subtitle}
                    </Typography>
                  )}
                </div>
                <Button
                  ref={buttonRef}
                  variant="outlined"
                  onClick={handleOpenPopover}
                  startIcon={<SettingsIcon fontSize="small" />}
                  endIcon={
                    <ArrowDropDownIcon
                      className={`${classes.buttonEndIcon} ${
                        isOpen ? classes.buttonEndIconOpen : ''
                      }`}
                    />
                  }
                  className={classes.configureButton}
                  size="small"
                  aria-label={`Configure ${label}`}
                >
                  Configure ({selectedItems.length})
                </Button>
              </div>

              {selectedItemsWithData.length === 0 ? (
                <Typography className={classes.emptyState}>
                  No items selected. Click "Configure" to select items.
                </Typography>
              ) : (
                <div className={classes.selectedItems}>
                  {visibleChips.map(item => (
                    <Chip
                      key={item.key}
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <span>{item.title}</span>
                          {showAutomation && item.hasAutomation && item.enableAutomation && (
                            <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>
                              (Automation)
                            </span>
                          )}
                        </Box>
                      }
                      size="small"
                      className={`${classes.chip} ${
                        showAutomation && item.hasAutomation && item.enableAutomation ? classes.automationChip : ''
                      }`}
                    />
                  ))}
                  {remainingCount > 0 && (
                    <Chip
                      label={`+${remainingCount} more`}
                      size="small"
                      className={classes.showMoreChip}
                    />
                  )}
                </div>
              )}

              {hasError && (
                <Typography className={classes.errorText}>
                  {backendError}
                </Typography>
              )}
            </div>

            <MultiSelectPopover
              anchorEl={anchorEl}
              open={isOpen}
              onClose={handleClosePopover}
              title={dialogTitle || `Select ${label}`}
              availableItems={availableItems}
              selectedItems={selectedItems}
              onApply={(selectedItems) => handleApply(selectedItems, form)}
              showAutomation={showAutomation}
              searchPlaceholder={searchPlaceholder}
            />
          </>
        );
      }}
    </Field>
  );
};

FormikMultiSelectPopover.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  availableItems: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    hasAutomation: PropTypes.bool,
  })).isRequired,
  showAutomation: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  dialogTitle: PropTypes.string,
  clearBackendError: PropTypes.func,
  backendError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  maxChipsDisplay: PropTypes.number,
};

FormikMultiSelectPopover.defaultProps = {
  subtitle: null,
  showAutomation: false,
  searchPlaceholder: 'Search items...',
  dialogTitle: null,
  clearBackendError: null,
  backendError: null,
  maxChipsDisplay: 5,
};

export default FormikMultiSelectPopover;
